import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  InputGroup,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { faClose, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';

import { DEFAULT_COLS_KEY } from 'src/models/AIIntegratedResultsTab.model';
import { getColumnKey } from 'src/models/ColumnConfiguration.model';
import { SortOrder, SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useGetExamGetPendingStatusText from 'src/hooks/useGetExamPendingStatusText';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';

import getSessionsImageFiles from 'src/utils/getSessionsImageFiles';
import { validateSearchValue } from 'src/utils/validationRules';

import SitesDropdown from '../custom_components/SitesDropdown';

const defaultPageSize = 10;

interface NewResultsPageProps {
  extraColumns?: Column<Session>[];
  currentPage: number;
  currentSearchValue: string;
  handleSetCurrentPage: (page: number) => void;
  handleSetCurrentSearchValue: (searchValue: string) => void;
  isLoading: boolean;
  dataResource: {
    sessions: Session[];
    totalElements: number;
    totalPages: number;
  };
  handleLoadSessions: (
    page: number,
    pageSize: number,
    searchValue: string,
    sessionsSortOrder: SortOrder,
    siteId?: string,
  ) => Promise<void>;
}

interface FormValues {
  searchValue: string;
}

const OHResults = ({
  extraColumns,
  currentPage,
  currentSearchValue,
  handleSetCurrentPage,
  handleSetCurrentSearchValue,
  isLoading,
  dataResource,
  handleLoadSessions,
}: NewResultsPageProps) => {
  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();

  const {
    setTeleophthResultsColsKeys,
    teleophthResultsColsKeys,
    selectedSiteId,
  } = useLocalStore();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      searchValue: '',
    },
  });

  const handleReset = () => {
    reset();
    handleSetCurrentPage(1);
    handleSetCurrentSearchValue('');
    handleLoadSessions(
      0,
      defaultPageSize,
      '',
      SortOrderEnum.DESC,
      selectedSiteId,
    );
  };
  const { t } = useTranslation(I18NextNS.PAGES);

  const handleSearch = useCallback(
    (data: { searchValue: string }) => {
      const { searchValue } = data;
      handleSetCurrentSearchValue(searchValue);
      handleLoadSessions(
        0,
        defaultPageSize,
        searchValue,
        SortOrderEnum.DESC,
        selectedSiteId,
      );
      handleSetCurrentPage(1);
    },
    [
      handleLoadSessions,
      handleSetCurrentPage,
      handleSetCurrentSearchValue,
      selectedSiteId,
    ],
  );

  const submitRef = useRef<HTMLFormElement>(null);

  const location = useLocation<{
    keyword: string;
  }>();

  useEffect(() => {
    if (location.state?.keyword) {
      setValue('searchValue', location.state?.keyword, {
        shouldValidate: true,
      });
      setFocus('searchValue');
    }
    getExistingSiteData();
    submitRef.current?.requestSubmit();
  }, [
    getExistingSiteData,
    handleLoadSessions,
    handleSearch,
    handleSubmit,
    location.state?.keyword,
    setFocus,
    setValue,
  ]);

  const getPendingStatusText = useGetExamGetPendingStatusText();

  const columns: Column<Session>[] = useMemo(
    () => [
      {
        title: t('patientFields.userEmail', { ns: I18NextNS.COMMON }),
        key: DEFAULT_COLS_KEY.USERNAME,
      },
      {
        title: t('patientFields.dateTime', { ns: I18NextNS.COMMON }),
        dataIndex: DEFAULT_COLS_KEY.DATE_TIME,
        render: (record: Session) => (
          <div>
            <b>{record.date}</b>
            <p>{record.time}</p>
          </div>
        ),
      },

      {
        title: t('patientFields.patientName', { ns: I18NextNS.COMMON }),
        shouldMask: true,
        dataIndex: DEFAULT_COLS_KEY.PATIENT_NAME,
        render: (record: Session) => (
          <div>
            {record.firstName} {record.lastName}
          </div>
        ),
      },
      {
        title: t('patientFields.patientID', { ns: I18NextNS.COMMON }),
        shouldMask: true,
        key: DEFAULT_COLS_KEY.ID,
      },
      {
        title: t('patientFields.DOB', { ns: I18NextNS.COMMON }),
        shouldMask: true,
        key: DEFAULT_COLS_KEY.DOB,
      },
      {
        title: (
          <div>
            <span>{t('results.sessionDetails')}</span>
            <p>({t('results.numberOfAttempts')})</p>
          </div>
        ),
        dataIndex: DEFAULT_COLS_KEY.ATTEMPTS,
        render: (record: Session) => {
          const { leftImageFileContainer, rightImageFileContainer } =
            getSessionsImageFiles([record, ...(record.retakeSessions ?? [])]);

          return (
            <div>
              {record.graded ? (
                <Stack gap={2}>
                  <div>
                    {t('newExam.leftEye')} (
                    {leftImageFileContainer.length > 0
                      ? leftImageFileContainer.length
                      : t('N/A', { ns: I18NextNS.COMMON })}
                    )
                  </div>
                  <div>
                    {t('newExam.rightEye')} (
                    {rightImageFileContainer.length > 0
                      ? rightImageFileContainer.length
                      : t('N/A', { ns: I18NextNS.COMMON })}
                    )
                  </div>
                </Stack>
              ) : (
                <Badge pill bg="secondary-200" className="text-black">
                  {getPendingStatusText(record)}
                </Badge>
              )}
            </div>
          );
        },
      },
      ...(extraColumns || []),
    ],
    [extraColumns, getPendingStatusText, t],
  );

  const defaultVisibleColumns = useMemo(() => {
    const defaultColumnsConfiguration = Object.fromEntries(
      columns.map((column) => [
        getColumnKey(column),
        getColumnKey(column) !== DEFAULT_COLS_KEY.USERNAME,
      ]),
    );
    return teleophthResultsColsKeys ?? defaultColumnsConfiguration;
  }, [teleophthResultsColsKeys, columns]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    defaultVisibleColumns,
  );

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = {
        ...prev,
        [key]: !prev[key],
      };

      setTeleophthResultsColsKeys(updatedVisibleColumns);
      return updatedVisibleColumns;
    });
  };

  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleColumns[getColumnKey(column)]),
    [columns, visibleColumns],
  );

  const history = useHistory();

  return (
    <Stack className="h-100" gap={3}>
      <Stack
        direction="horizontal"
        gap={4}
        className="flex-wrap align-items-end"
      >
        <div>
          <SitesDropdown
            isLoadingSites={isLoadingSites}
            existingSites={existingSites}
            handleSiteChange={(siteId: string | undefined) => {
              handleLoadSessions(
                0,
                defaultPageSize,
                currentSearchValue,
                SortOrderEnum.DESC,
                siteId,
              );
            }}
          />
        </div>

        <Form
          onSubmit={handleSubmit(handleSearch)}
          ref={submitRef}
          className="flex-fill"
        >
          <Form.Group as={Col} controlId="searchValue">
            <InputGroup>
              <Form.Control
                autoComplete="off"
                type="text"
                className="rounded-3 me-2"
                placeholder={t('results.searchPlaceholder')}
                {...register('searchValue', {
                  maxLength: {
                    value: 100,
                    message: t('results.searchMaxLength'),
                  },
                  validate: {
                    isValid: (value) =>
                      validateSearchValue(value) || t('results.searchInvalid'),
                  },
                  onChange: (event) =>
                    setValue('searchValue', event.target.value, {
                      shouldValidate: true,
                    }),
                })}
              />

              <ButtonGroup size="sm">
                <Button variant="outline-primary" type="submit">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className={classNames({
                      'icon-shake': isValid && getValues('searchValue'),
                    })}
                  />
                </Button>
                <Button
                  variant="outline-warning"
                  onClick={handleReset}
                  disabled={!getValues('searchValue')}
                  data-test="reset-button"
                >
                  <FontAwesomeIcon icon={faClose} />
                </Button>
              </ButtonGroup>
            </InputGroup>
          </Form.Group>

          {errors.searchValue?.message && (
            <p className="text-danger">{errors.searchValue?.message}</p>
          )}
        </Form>

        <ColumnConfiguration
          columns={columns}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
        />
      </Stack>
      <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          <CustomTable
            isLoading={isLoading}
            dataSource={dataResource.sessions}
            rowKey="iid"
            columns={displayedColumns}
            pagination={{
              defaultPageSize,
              pageSizeOptions: [10],
              totalPages: dataResource.totalPages,
              currentPage,
              handleSetCurrentPage,
              onChangePage: (pageNumber, pageSize) => {
                handleLoadSessions(
                  pageNumber - 1,
                  pageSize,
                  currentSearchValue,
                  SortOrderEnum.DESC,
                  selectedSiteId,
                );
              },
            }}
            empty={{
              buttonText: t('results.createNewExam'),
              onButtonClick: () => {
                history.push('/session-form');
              },
            }}
          />
        </div>
      </div>
    </Stack>
  );
};

export default OHResults;
